import { format, parseISO } from 'date-fns';
import { TFunction } from 'i18next';

export const DATE_INPUT_MAXLENGTH = 10;
export const DATE_MM_DD_YYYY = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const DATE_INPUT_DIGIT_COUNT = 8;
const FULL_DATE_PATTERN = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/;
const ISO_DATE_PATTERN = /^(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})$/;

/**
 * Masks raw, incomplete, or pasted keystrokes into a MM/DD/YYYY layout as the user
 * types, inserting slashes automatically so manual entry produces a value
 * that matches DATE_MM_DD_YYYY (e.g. '01011990' -> '01/01/1990').
 *
 * Pads single-digit month or day when followed by a delimiter (e.g. '1/' -> '01/',
 * '01/5/' -> '01/05/') and full M/D/YYYY or ISO pastes (e.g. '1/5/1990' -> '01/05/1990').
 */
export const formatDateInputText = (text: string): string => {
  if (!text) {
    return '';
  }

  const isoMatch = text.match(ISO_DATE_PATTERN);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
  }

  const fullMatch = text.match(FULL_DATE_PATTERN);
  if (fullMatch) {
    const [, month, day, year] = fullMatch;
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
  }

  // Auto-pad single-digit month or day when followed by a delimiter (e.g. '1/' -> '01/', '01/5/' -> '01/05/')
  const normalized = text
    .replace(/^(\d)[/\-.]/, '0$1/')
    .replace(/^(\d{2})[/\-.](\d)[/\-.]/, '$1/0$2/');

  const endsWithSlash = normalized.endsWith('/');
  const digits = normalized.replace(/\D/g, '').slice(0, DATE_INPUT_DIGIT_COUNT);

  if (digits.length < 2) {
    return endsWithSlash && digits ? `${digits}/` : digits;
  }
  if (digits.length === 2) {
    return endsWithSlash ? `${digits}/` : digits;
  }
  if (digits.length <= 4) {
    const formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return endsWithSlash && digits.length === 4 ? `${formatted}/` : formatted;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

/**
 * Formats a given utc string (eg. '2019-06-28T04:51:20.123Z')
 * or date to the provided format by default to 'MM/dd/yyyy'
 */
export const formatDate = (
  dateStr: string | Date,
  dateFormat = 'MM/dd/yyyy',
): string => {
  const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
  return format(date, dateFormat);
};

export const formatLocalDate = (
  dateStr: string | Date,
  dateFormat = 'MMM dd, yyyy',
): string => {
  const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
  return format(date, dateFormat);
};

/**
 * Considering input in yyyy-MM-dd
 * Change date format yyyy-MM-dd to MM-dd-yyyy
 */
export const changeDateFormat = (dateString: string) => {
  if (!dateString) {
    return '';
  }
  const [year, month, day] = dateString.split('-');
  const formattedMonth = String(month).padStart(2, '0');
  return `${formattedMonth}-${day}-${year}`;
};

export const formatDateWithSlash = (date: string): string => {
  return date.replaceAll('-', '/');
};

export const formatDisplayDate = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  };
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

export const formatDisplayDateByMonthYear = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
  };
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

export const monthYearFormatDateUtil = (
  dateString: string,
  translatedText: TFunction,
) => {
  const date = new Date(dateString);
  const months = [
    translatedText('Jan'),
    translatedText('Feb'),
    translatedText('Mar'),
    translatedText('Apr'),
    translatedText('May'),
    translatedText('Jun'),
    translatedText('Jul'),
    translatedText('Aug'),
    translatedText('Sep'),
    translatedText('Oct'),
    translatedText('Nov'),
    translatedText('Dec'),
  ];

  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear().toString().slice(-2);

  return `${month} ${year}`;
};
